import { useState } from 'react'
import { Lock, Power, RotateCw, Trash2 } from 'lucide-react'
import type { BrokerMode, BrokerStatus, GatewayPhase } from '@shared/broker'
import { brokerApi } from '../../lib/api'
import { useBrokerStream } from '../../hooks/useBrokerStream'
import { cn } from '../../lib/utils'

const input =
  'min-w-0 flex-1 rounded-lg border border-[var(--line-soft)] bg-[var(--color-inset)] px-3 py-2 text-[13px] text-fg placeholder:text-fg-faint focus:outline-none'
const btn = 'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[12.5px] font-medium transition-colors disabled:opacity-40'

const PHASE_LABEL: Record<GatewayPhase, string> = {
  absent: 'Not created',
  stopped: 'Stopped',
  starting: 'Starting — signing in…',
  awaiting_2fa: 'Waiting for 2FA — approve the login in IBKR Mobile',
  running: 'Running',
  login_failed: 'Login failed — check username/password',
  error: 'Error',
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 text-[12.5px]">
      <span className="shrink-0 text-fg-subtle">{label}</span>
      <span className="min-w-0 text-right text-fg-muted">{children}</span>
    </div>
  )
}

function Dot({ tone }: { tone: 'ok' | 'wait' | 'off' | 'bad' }) {
  const c = tone === 'ok' ? 'bg-up' : tone === 'bad' ? 'bg-down' : tone === 'wait' ? 'bg-fg-muted animate-pulse' : 'bg-flat'
  return <span className={cn('mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle', c)} />
}

export function BrokerSettings() {
  const { snapshot } = useBrokerStream()
  const [override, setOverride] = useState<BrokerStatus | null>(null)
  const status = snapshot?.status ?? override
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [mode, setMode] = useState<BrokerMode>('paper')
  const [busy, setBusy] = useState<null | 'save' | 'start' | 'stop' | 'delete'>(null)
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null)
  const [showAdvanced, setShowAdvanced] = useState(false)

  const run = (kind: NonNullable<typeof busy>, fn: () => Promise<BrokerStatus>, okText: string) => async () => {
    setBusy(kind)
    setMsg(null)
    try {
      setOverride(await fn())
      setMsg({ tone: 'ok', text: okText })
    } catch (e) {
      setMsg({ tone: 'err', text: e instanceof Error ? e.message : 'Something went wrong' })
    } finally {
      setBusy(null)
    }
  }

  const onSave = run(
    'save',
    async () => {
      const s = await brokerApi.saveCredentials(username.trim(), password, mode)
      // Drop the plaintext from component state the moment it has been sealed server-side.
      setPassword('')
      setUsername('')
      return s
    },
    'Saved to the encrypted vault. The gateway is signing in — approve the login in IBKR Mobile.',
  )
  const onStart = run('start', brokerApi.startGateway, 'Gateway starting — approve the login in IBKR Mobile.')
  const onStop = run('stop', brokerApi.stopGateway, 'Gateway stopped.')
  const onDelete = run('delete', brokerApi.deleteCredentials, 'Login removed from the vault and gateway stopped.')

  const cred = status?.credentials
  const gw = status?.gateway
  const connected = status?.connection === 'connected'
  const phaseTone = !gw
    ? 'off'
    : gw.phase === 'running'
      ? 'ok'
      : gw.phase === 'awaiting_2fa' || gw.phase === 'starting'
        ? 'wait'
        : gw.phase === 'login_failed' || gw.phase === 'error'
          ? 'bad'
          : 'off'

  return (
    <section id="settings-broker" className="card p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-[14px] font-semibold text-fg">Broker · Interactive Brokers</h2>
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide',
            connected ? 'bg-[var(--accent-wash)] text-fg' : 'text-fg-faint',
          )}
        >
          {connected ? (status?.isPaper ? 'Connected · Paper' : 'Connected') : 'Not connected'}
        </span>
      </div>
      <p className="mt-0.5 text-[12.5px] text-fg-subtle">
        <Lock size={11} className="mr-1 inline align-[-1px]" />
        Your IBKR login is encrypted on the server (AES-256-GCM, key never leaves the machine) and is never shown again —
        not to you, not to the AI. The gateway signs in with it; you approve each login in <b className="text-fg-muted">IBKR Mobile</b>.
      </p>

      {status && (
        <div className="mt-3 divide-y divide-[var(--separator)] rounded-lg border border-[var(--line-soft)] px-3">
          <Row label="Vault">
            {cred?.configured ? (
              <>
                <Dot tone="ok" />
                Stored · <span className="num">{cred.usernameHint}</span> · {cred.mode}
                {!cred.enabled && <span className="text-fg-faint"> (paused)</span>}
              </>
            ) : (
              <>
                <Dot tone="off" />
                No login stored
              </>
            )}
          </Row>
          <Row label="Gateway">
            <Dot tone={phaseTone} />
            {gw ? PHASE_LABEL[gw.phase] : '—'}
          </Row>
          <Row label="API session">
            <Dot tone={connected ? 'ok' : status.connection === 'connecting' ? 'wait' : 'off'} />
            {status.connection}
          </Row>
          {status.accounts.length > 0 && (
            <Row label="Account">
              <span className="num">{status.accounts.join(', ')}</span>{' '}
              <span className={cn('ml-1 rounded px-1 text-[10px] font-semibold uppercase', status.isPaper ? 'bg-[var(--color-inset)] text-fg-muted' : 'text-down')}>
                {status.isPaper ? 'paper' : 'NOT paper'}
              </span>
            </Row>
          )}
          <Row label="Orders">
            {status.tradingEnabled ? (
              <>
                <Dot tone="ok" />
                Enabled (paper)
              </>
            ) : (
              <>
                <Dot tone="off" />
                {status.tradingBlockedReason ?? 'Blocked'}
              </>
            )}
          </Row>
          {status.lastError && (
            <Row label="Last error">
              <span className="text-down">{status.lastError}</span>
            </Row>
          )}
        </div>
      )}

      <div className="mt-3 space-y-2">
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder={cred?.configured ? 'IBKR username — type to replace' : 'IBKR username'}
          autoComplete="off"
          spellCheck={false}
          className={input}
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={cred?.configured ? 'Password stored — type to replace' : 'IBKR password'}
          autoComplete="new-password"
          className={input}
        />
        <div className="flex items-center gap-2 text-[12.5px]">
          <span className="text-fg-subtle">Mode</span>
          <div className="inline-flex rounded-lg border border-[var(--line-soft)] p-0.5">
            {(['paper', 'live'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                disabled={m === 'live' && !status?.liveAllowed}
                title={m === 'live' && !status?.liveAllowed ? 'Live trading is disabled on this server' : undefined}
                className={cn(
                  'rounded-md px-3 py-1 font-medium capitalize transition-colors disabled:cursor-not-allowed disabled:opacity-35',
                  mode === m ? 'bg-[var(--color-surface-3)] text-fg' : 'text-fg-subtle hover:text-fg',
                )}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <button
          onClick={onSave}
          disabled={!!busy || !username.trim() || !password}
          className={cn(btn, 'bg-[var(--color-accent)] text-[var(--color-base)]')}
        >
          <Lock size={13} /> {busy === 'save' ? 'Encrypting…' : 'Save & connect'}
        </button>
        {cred?.configured && (
          <>
            <button onClick={onStart} disabled={!!busy} className={cn(btn, 'border border-[var(--line-soft)] text-fg-muted hover:text-fg')}>
              <RotateCw size={13} /> {busy === 'start' ? 'Starting…' : gw && gw.phase !== 'absent' && gw.phase !== 'stopped' ? 'Restart gateway' : 'Start gateway'}
            </button>
            <button onClick={onStop} disabled={!!busy} className={cn(btn, 'border border-[var(--line-soft)] text-fg-muted hover:text-fg')}>
              <Power size={13} /> {busy === 'stop' ? 'Stopping…' : 'Stop'}
            </button>
            <button
              onClick={() => confirm('Remove the stored IBKR login and stop the gateway?') && void onDelete()}
              disabled={!!busy}
              className={cn(btn, 'text-down hover:bg-[var(--color-surface-2)]')}
            >
              <Trash2 size={13} /> Remove login
            </button>
          </>
        )}
      </div>
      {gw && (gw.phase === 'running' || gw.phase === 'awaiting_2fa') && (
        <p className="mt-2 text-[11.5px] text-fg-faint">Restarting the gateway requires a new IBKR Mobile approval.</p>
      )}
      {msg && <div className={cn('mt-2 text-[12px]', msg.tone === 'ok' ? 'text-up' : 'text-down')}>{msg.text}</div>}

      {gw && gw.phase !== 'absent' && (
        <div className="mt-3">
          <button onClick={() => setShowAdvanced((v) => !v)} className="text-[12px] font-medium text-fg-subtle hover:text-fg">
            {showAdvanced ? '▾' : '▸'} Advanced — gateway log & emergency access
          </button>
          {showAdvanced && (
            <div className="mt-2 space-y-2">
              <pre className="max-h-56 overflow-auto rounded-lg bg-[var(--color-inset)] p-2.5 text-[11px] leading-relaxed text-fg-muted">
                {gw.logTail.length ? gw.logTail.join('\n') : 'No log output yet.'}
              </pre>
              {gw.vncPassword && (
                <div className="text-[11.5px] leading-relaxed text-fg-subtle">
                  Emergency GUI (only if IBKR asks for something the automation can't answer): open an SSH tunnel{' '}
                  <code className="rounded bg-[var(--color-inset)] px-1">ssh -L 5900:127.0.0.1:5900 supra</code>, then connect a
                  VNC viewer to <code className="rounded bg-[var(--color-inset)] px-1">localhost:5900</code> with password{' '}
                  <code className="num rounded bg-[var(--color-inset)] px-1">{gw.vncPassword}</code>. The port is never exposed to the internet.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
